export type GoLiveMessage = {
  id: string
  title: string
  joke: string
  symbol: string
  palette: string
  pattern: 'orbit' | 'grid' | 'waves' | 'rays' | 'confetti'
}

// The Go live window shows one of these each time. For now Live now is for
// mentors, so each one is about a mentor who meets another mentor.
export const GO_LIVE_MESSAGES: GoLiveMessage[] = [
  {
    id: 'mission-control',
    title: 'Houston, we have company.',
    joke: 'Another mentor is in orbit right now. Say hello before they pass over.',
    symbol: '🚀',
    palette: 'bg-neutral-900 text-white',
    pattern: 'orbit',
  },
  {
    id: 'coffee',
    title: 'The coffee chat, minus the queue.',
    joke: 'Fifteen minutes with a mentor you have not met. Bring your own cup.',
    symbol: '☕',
    palette: 'bg-warning-50 text-neutral-900',
    pattern: 'waves',
  },
  {
    id: 'rubber-duck',
    title: 'Birds of a feather.',
    joke: 'A whole pond of people who do what you do. Paddle over and say hi.',
    symbol: '🦆',
    palette: 'bg-primary-100 text-primary-900',
    pattern: 'waves',
  },
  {
    id: 'lightbulb',
    title: 'Two bright ideas walk into a call.',
    joke: 'One of you has seen this before. Fifteen minutes to find out which.',
    symbol: '💡',
    palette: 'bg-warning-100 text-neutral-900',
    pattern: 'rays',
  },
  {
    id: 'cactus',
    title: 'Less prickly than a networking event.',
    joke: 'No name badge. No talk about the weather. One mentor, one call.',
    symbol: '🌵',
    palette: 'bg-success-50 text-success-900',
    pattern: 'rays',
  },
  {
    id: 'disco',
    title: 'The mixer is on.',
    joke: 'Find someone on the floor and say hello. Terrible dancing is optional.',
    symbol: '🪩',
    palette: 'bg-secondary-900 text-white',
    pattern: 'grid',
  },
  {
    id: 'toast',
    title: 'You might be someone’s jam.',
    joke: 'A mentor out there does the other half of what you do. Go and meet them.',
    symbol: '🍞',
    palette: 'bg-warning-50 text-neutral-900',
    pattern: 'grid',
  },
  {
    id: 'astronaut',
    title: 'Less space between mentors.',
    joke: 'You all work in the same universe and have never met. Fix that today.',
    symbol: '🧑‍🚀',
    palette: 'bg-primary-900 text-white',
    pattern: 'confetti',
  },
  {
    id: 'magic',
    title: 'No wand. Just an introduction.',
    joke: 'Clients, collaborations and friendships tend to start with one hello.',
    symbol: '🪄',
    palette: 'bg-secondary-50 text-secondary-900',
    pattern: 'rays',
  },
  {
    id: 'telescope',
    title: 'See who else is out there.',
    joke: 'Hundreds of mentors are on GrowthMentor. How many have you met?',
    symbol: '🔭',
    palette: 'bg-neutral-900 text-white',
    pattern: 'confetti',
  },
  {
    id: 'pizza',
    title: 'Grab a slice with a mentor.',
    joke: 'Trade one thing that worked and one that flopped. Pineapple stays out of it.',
    symbol: '🍕',
    palette: 'bg-error-50 text-neutral-900',
    pattern: 'grid',
  },
  {
    id: 'toolbox',
    title: 'Swap tools with a pro.',
    joke: 'They have a trick you have never tried. You have one they need.',
    symbol: '🧰',
    palette: 'bg-primary-50 text-primary-900',
    pattern: 'grid',
  },
  {
    id: 'speech',
    title: 'Talk shop with someone who gets it.',
    joke: 'No need to explain what a mentor does. Skip to the good part.',
    symbol: '💬',
    palette: 'bg-secondary-100 text-secondary-900',
    pattern: 'orbit',
  },
  {
    id: 'umbrella',
    title: 'Forecast: a chance of new friends.',
    joke: 'Light conversation, with a possible referral later in the week.',
    symbol: '☂️',
    palette: 'bg-primary-100 text-primary-900',
    pattern: 'confetti',
  },
  {
    id: 'door',
    title: 'Leave the door open.',
    joke: 'The next person through it could be a collaborator, a client or a friend.',
    symbol: '🚪',
    palette: 'bg-success-100 text-success-900',
    pattern: 'orbit',
  },
  {
    id: 'paper-plane',
    title: 'Send a hello somewhere.',
    joke: 'It lands with a mentor in another city and another field. See where it goes.',
    symbol: '🛩️',
    palette: 'bg-primary-50 text-primary-900',
    pattern: 'waves',
  },
  {
    id: 'campfire',
    title: 'Pull up a log with the other mentors.',
    joke: 'Swap stories from the trenches. Marshmallows remain your responsibility.',
    symbol: '🔥',
    palette: 'bg-neutral-800 text-warning-50',
    pattern: 'rays',
  },
  {
    id: 'brain',
    title: 'Two brains that do this for a living.',
    joke: 'Ask how they run their calls. Steal the good parts. They will do the same.',
    symbol: '🧠',
    palette: 'bg-error-50 text-neutral-900',
    pattern: 'waves',
  },
  {
    id: 'record',
    title: 'Meet someone outside your playlist.',
    joke: 'A mentor from another field can change the track. No DJ skills needed.',
    symbol: '🎧',
    palette: 'bg-secondary-900 text-white',
    pattern: 'orbit',
  },
  {
    id: 'balloon',
    title: 'Float an idea past a mentor.',
    joke: 'The project you keep thinking about? One of them may want in.',
    symbol: '🎈',
    palette: 'bg-warning-50 text-neutral-900',
    pattern: 'confetti',
  },
]

const STORAGE_KEY = 'gm-go-live-messages-v2'
let remaining: string[] = []
let previous: string | null = null
let loaded = false

// A shuffled deck: every message appears once before a new cycle. Persist only
// message IDs, so returning to the page does not restart the same first joke.
export function nextGoLiveMessage(): GoLiveMessage {
  const ids = GO_LIVE_MESSAGES.map((message) => message.id)
  if (!loaded) {
    loaded = true
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null')
      if (saved && Array.isArray(saved.remaining)) {
        remaining = [
          ...new Set<string>(
            saved.remaining.filter(
              (id: unknown) => typeof id === 'string' && ids.includes(id),
            ),
          ),
        ]
        previous = ids.includes(saved.previous) ? saved.previous : null
      }
    } catch {
      /* Storage can be unavailable; the in-memory deck still works. */
    }
  }
  if (!remaining.length) {
    const palette = (id: string | null) =>
      GO_LIVE_MESSAGES.find((message) => message.id === id)?.palette
    const hasAdjacentColors = (deck: string[]) =>
      deck.some(
        (id, index) =>
          palette(id) === palette(index ? deck[index - 1] : previous),
      )
    for (let attempt = 0; attempt < 100; attempt++) {
      remaining = [...ids]
      for (let i = remaining.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[remaining[i], remaining[j]] = [remaining[j], remaining[i]]
      }
      if (!hasAdjacentColors(remaining)) break
    }
    // The authored order also alternates colors, including its last/first pair.
    // This bounded fallback handles an unlucky or deterministic random source.
    if (hasAdjacentColors(remaining)) {
      remaining = [...ids]
      if (palette(remaining[0]) === palette(previous)) {
        const first = remaining.shift()
        if (first) remaining.push(first)
      }
    }
  }
  const id = remaining.shift() ?? ids[0]
  previous = id
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ remaining, previous }))
  } catch {
    /* Use memory. */
  }
  return (
    GO_LIVE_MESSAGES.find((message) => message.id === id) ?? GO_LIVE_MESSAGES[0]
  )
}
